import { r, s } from '@/theme/tokens';
import { Box, Skeleton } from '@/ui/primitives';
import { MetricTrendCardFrame } from './MetricTrendCardFrame';
import { metricTrendCardCompactLayout } from './MetricTrendCardFrame.layout';
import type { SxProps, Theme } from '@/ui/primitives';
import type { ReactElement } from 'react';

const cardSkeletonBarHeight = 12;
const cardSkeletonLabelHeight = 12;
const cardSkeletonValueHeight = 12;
const cardSkeletonValueWidth = 58;

type MetricTrendCardSkeletonProps = {
  showCornerIconSkeleton?: boolean;
  minHeight?: number;
  sx?: Exclude<Extract<SxProps<Theme>, readonly unknown[]>[number], boolean>;
};

export const MetricTrendCardSkeleton = ({
  showCornerIconSkeleton = true,
  minHeight = metricTrendCardCompactLayout.minHeight,
  sx,
}: MetricTrendCardSkeletonProps): ReactElement => {
  const titleNode = (
    <Skeleton
      variant="rounded"
      width={92}
      height={cardSkeletonBarHeight}
      sx={{ borderRadius: r('pill') }}
    />
  );
  const footerNode = (
    <Skeleton
      variant="rounded"
      width="min(100%, 84px)"
      height={cardSkeletonLabelHeight}
      sx={{ borderRadius: r('pill') }}
    />
  );
  const valueNode = (
    <Skeleton
      variant="rounded"
      width={cardSkeletonValueWidth}
      height={cardSkeletonValueHeight}
      sx={{ borderRadius: r('pill') }}
    />
  );

  return (
    <MetricTrendCardFrame
      title={titleNode}
      cornerIcon={
        showCornerIconSkeleton ? <Skeleton variant="circular" width={18} height={18} /> : null
      }
      titleFontSize={undefined}
      sx={sx}
      minHeight={minHeight}
      chartHeight={metricTrendCardCompactLayout.chartHeight}
      fixedHeight
      headerPaddingX={metricTrendCardCompactLayout.headerPaddingX}
      headerPaddingTop={s('md')}
      headerPaddingBottom={s('none')}
      middleGridTemplateColumns={metricTrendCardCompactLayout.middleGridTemplateColumns}
      middleColumnGap={metricTrendCardCompactLayout.middleColumnGap}
      middlePaddingY={s('sm')}
      value={valueNode}
      valuePaddingX={metricTrendCardCompactLayout.valuePaddingX}
      valuePaddingRight={metricTrendCardCompactLayout.valuePaddingRight}
      valuePaddingY={metricTrendCardCompactLayout.valuePaddingY}
      chart={<Box aria-hidden="true" sx={{ height: metricTrendCardCompactLayout.chartHeight }} />}
      chartPaddingX={metricTrendCardCompactLayout.chartPaddingX}
      chartPaddingLeft={metricTrendCardCompactLayout.chartPaddingLeft}
      footer={footerNode}
      footerPaddingX={metricTrendCardCompactLayout.footerPaddingX}
      footerPaddingTop={s('none')}
      footerPaddingBottom={s('md')}
      footerSx={{ gap: s('sm') }}
    />
  );
};
